import React, {useState, useEffect, forwardRef, useRef, useImperativeHandle} from 'react';
import {
    setInputValue as procSetText,
    openProcChoose,
    openProcSearch,
    procSearch,
    procFormZoom,
    procZoomToAdvancedSearch,
    procSearchNext
} from '../actions/procSearch';
import {getText} from '../../../_shared/GWTBridge/invoke/procMain';
import {
    navigateToNextField,
    onFieldFocus,
    invokeF9Handler
} from '../../../_shared/GWTBridge/invoke/priInputDlg';
import {SearchInput} from '../../FormHeader/components/Search/SearchInput';
import {TagsArea} from '../../../components/Common/TagsArea';
import {
    Button,
    PriorityIcon,
    Text,
    Icon,
    Flex,
    noop
} from '@priority-software/priority-style-react';
import {ProcOperator} from '@priority-software/priority-utils/lib/operators';
import {
    SearchHandlers,
    SearchState,
    useSearch
} from '../../../components/Common/FieldSearch/hooks/useSearch';
import {useSelect} from '../../../components/Common/FieldSearch/hooks/useSelect';
import {InputDropdown} from './InputDropdown';
import {ZoomTypes} from '../consts/zoomTypes';
import {setIsCloseKeysBlocked} from '../../../_shared/GWTBridge/invoke/priModalDlg';
import texts from '../texts/programRep/texts';
import {onHyperlinkClick} from '../../../_shared/GWTBridge/invoke/priTextBox';
import {helpPopoverRef} from '../../../_shared/Help/constants';
import {showInfoMessage} from '../../../_shared/Messages/actions/MessageActions';
import {DropdownContent} from './ProcDropdownContent';
import {getLocalizedText, texts as generalTexts} from '../../../constants/TextConst';
import styles from '../styles/ProcSearch.module.scss';
import {getDropdownMaxHeight} from '../../../components/Common/FieldSearch/utils/dropdownStyle';
import {Direction} from '../../../components/Common/FieldSearch/utils/search';
import {messagesIds} from '../../../_shared/Messages/consts';
import {getRawDate, isValid} from '@priority-software/priority-utils/lib/date';

const PAGE_BOTTOM_OFFSET = 10; // distance we should keep from bottom of the page
const DD_RESULTS_MIN_HEIGHT = 240;
const DD_RESULTS_MAX_HEIGHT = 300;
const DD_ELEMENTS_HEIGHT = 115;

export interface ProcSearchInitProps {
    autoFocus: boolean;
    canZoom: boolean;
    defaultDir: string;
    element: HTMLInputElement;
    fieldNum: number;
    help: {helpText: string; helpLinks: any};
    inputIdx: number;
    row: number;
    zoomtype: number;
    operator?: ProcOperator;
    isDate?: boolean;
    dateFormat?: string;
    isSimpleChoose?: boolean;
}

export interface ProcSearchProps extends ProcSearchInitProps {
    handleFocusField: () => void;
    handleActivateField: () => void;
    handleZoom: () => void;
    inputRef: React.RefObject<HTMLInputElement>;
}

const ProcSearchInput = forwardRef((props: ProcSearchProps, ref) => {
    const {
        fieldNum,
        inputIdx,
        canZoom,
        handleFocusField,
        handleActivateField,
        handleZoom,
        inputRef,
        isDate,
        dateFormat
    } = props;

    // state
    const [inputValue, setInputValue] = useState(getText(fieldNum, inputIdx));

    //handlers

    const handleOnChange = value => {
        let nextValue = value;
        if (isDate) {
            const rawDate = getRawDate(inputValue, value, dateFormat);
            nextValue = isValid(rawDate, dateFormat) ? rawDate : inputValue;
        }
        setInputValue(nextValue);
        procSetText(nextValue, fieldNum, inputIdx);
    };

    const handleKeyDown = event => {
        const {code} = event;
        switch (code) {
            case 'F6':
                canZoom && handleZoom();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.addEventListener('keydown', handleKeyDown);

            const cleanupCopy = inputRef.current;
            return () => cleanupCopy.removeEventListener('keydown', handleKeyDown);
        }
        return noop;
    }, [inputRef.current]);

    useImperativeHandle(
        ref,
        () => {
            return {
                resetSearchQuery: value => setInputValue(value),
                activateField: handleActivateField
            };
        },
        [inputValue]
    );

    return (
        <SearchInput
            inputRef={inputRef}
            onChange={handleOnChange}
            value={inputValue}
            icon={
                canZoom ? (
                    <Icon
                        icon={PriorityIcon.searchThin}
                        onClick={handleZoom}
                        iconColor="secondary-400"
                    />
                ) : null
            }
            onFocus={handleFocusField}
            autoFocus={props.autoFocus}
            autoSelect={props.autoFocus}
            size="small"
            componentClasses={styles.input}
            direction={props.defaultDir as Direction}
        />
    );
});

const ProcSearchDropdown = forwardRef((props: ProcSearchProps, ref) => {
    //refs
    const isOpening = useRef(false);
    const cachedValue = useRef('');
    const {
        fieldNum,
        inputIdx,
        zoomtype,
        canZoom,
        handleFocusField,
        handleActivateField,
        inputRef,
        operator,
        isSimpleChoose
    } = props;

    // dropdown state
    const [isOpen, setIsOpen] = useState(false);

    const openChooseOrSearch = searchQuery => {
        return zoomtype === ZoomTypes.Search
            ? openProcSearch(fieldNum, searchQuery)
            : openProcChoose(fieldNum, searchQuery);
    };

    // search actions
    const searchActions = {
        onSearchOpen: (isForceSearch, searchQuery) => openChooseOrSearch(searchQuery),
        onSearchAction: (action, searchQuery, stackCursor, searchStart) =>
            procSearch(fieldNum, searchQuery, action, stackCursor, searchStart),
        onScroll: (page, searchQuery, stackCursor, searchStart) =>
            procSearchNext(fieldNum, searchQuery, page, stackCursor, searchStart)
    };

    const isMultiSelect = canZoom && operator === ProcOperator.Equals;

    // search state
    const inputValue = getText(fieldNum, inputIdx);
    const [searchState, searchHandlers] = useSearch(searchActions, inputValue, {
        showWildCardOptions: true,
        isForceMulti: isMultiSelect
    }) as [SearchState, SearchHandlers];

    // search handlers
    const {
        handleSearchOpen,
        handleSearchChange,
        handleScroll,
        handleSortByChange,
        handleIgnoreCaseChange,
        handleNavigationKeys,
        handleSearchByChange,
        handleSearchFromBeginningChange,
        handleSearchQueryChange
    } = searchHandlers as SearchHandlers;

    const handleInputChange = value => {
        if (isOpen) {
            handleSearchChange(value);
        } else {
            if (!searchState.searchQuery.length && value === ' ') {
                handleOpen();
            } else {
                handleSearchQueryChange(value);
            }
        }
    };

    const handleSpreadsheetValues = ({values}) => {
        if (isMultiSelect) {
            const newValues = [...selected, ...values]
                .map(value => {
                    if (!value.value) {
                        return {value};
                    }
                    return value;
                })
                .reduce((unique, o) => {
                    if (!unique.some(obj => obj.value === o.value)) {
                        unique.push(o);
                    }
                    return unique;
                }, []);
            return handleResetItems(newValues);
        }
        showInfoMessage({
            id: messagesIds.searches.pasteWarn,
            title: getLocalizedText(generalTexts.pasteWarn),
            confirmButtonText: getLocalizedText(generalTexts.confirmLabel)
        });
    };

    // select actions
    const onSelect = value => {
        handleSearchQueryChange(value);
        handleClose();
    };

    const onMultiSelect = values => {
        handleClose();
    };

    const selectActions = {
        onSelect,
        onMultiSelect
    };

    const selectedLimit = {
        limit: 50,
        limitCallback: () => {
            // If message is on a gwt form make sure enter closes only message
            setIsCloseKeysBlocked(true);
            showInfoMessage({
                title: texts.over50,
                onClose: () => {
                    setIsCloseKeysBlocked(false);
                }
            });
        }
    };
    // select state
    const {selected, isItemSelected, selectHandlers} = useSelect(
        searchState,
        selectActions,
        selectedLimit
    );

    useImperativeHandle(
        ref,
        () => {
            return {
                resetTags: handleResetItems,
                resetSearchQuery: value => handleSearchQueryChange(value),
                activateField: handleActivateField
            };
        },
        [selected, searchState.searchQuery]
    );

    // select handlers
    const {
        handleSelectItem,
        handleClearItem,
        handleMultiSelect,
        handleSelectKeys,
        handleResetItems
    } = selectHandlers;

    //handlers

    const handleOpen = async () => {
        if (isOpening.current) return;
        try {
            isOpening.current = true;
            cachedValue.current = searchState.searchQuery;
            await handleSearchOpen(searchState.searchQuery);
        } finally {
            isOpening.current = false;
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        inputRef.current?.focus();
    };

    const handleKeyClose = () => {
        if (cachedValue.current !== searchState.searchQuery) {
            handleSearchQueryChange(cachedValue.current);
        }
        handleClose();
    };

    useEffect(() => {
        setIsCloseKeysBlocked(isOpen);
        if (isMultiSelect && selected?.length) {
            handleSearchQueryChange();
        }
    }, [isOpen, selected]);

    useEffect(() => {
        if (isOpen || cachedValue.current?.length) {
            inputRef?.current?.select();
        }
    }, [isOpen]);

    const handleZoomToAdvancedSearch = () => {
        handleClose();
        handleResetItems();
        procZoomToAdvancedSearch(fieldNum, inputIdx);
    };

    const handleDropdownKeyDown = event => {
        handleSelectKeys(event);
        handleNavigationKeys(event);
    };

    const handleZoom = () => {
        handleResetItems();
        props.handleZoom();
    };

    const handleKeyDown = event => {
        const {code} = event;
        switch (code) {
            case 'F6':
                if (!isOpen && !isOpening.current) {
                    handleOpen();
                } else {
                    isOpen && handleClose();
                    canZoom && handleZoom();
                }
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.addEventListener('keydown', handleKeyDown);

            const cleanupCopy = inputRef.current;
            return () => cleanupCopy.removeEventListener('keydown', handleKeyDown);
        }
        return noop;
    }, [inputRef.current, isOpen, searchState.searchQuery]);

    useEffect(() => {
        if (!searchState.isLoading) {
            if (searchState.isZoom) {
                handleZoom();
            } else {
                setIsOpen(true);
            }
        }
    }, [searchState.isLoading]);

    useEffect(() => {
        if (isMultiSelect && selected?.length) {
            const values = selected.map(t => t.value);
            const value = values.join(' OR ');
            procSetText(value, fieldNum, inputIdx);
        }
    }, [selected]);

    useEffect(() => {
        if (!selected || !selected.length) {
            procSetText(searchState.searchQuery, fieldNum, inputIdx);
        }
    }, [searchState.searchQuery, selected]);

    const style = isOpen && canZoom ? {width: 520} : {};

    const elementRect = inputRef.current?.getBoundingClientRect();
    const resultsAutoMaxHeight = elementRect
        ? getDropdownMaxHeight(
              elementRect,
              DD_RESULTS_MAX_HEIGHT,
              DD_RESULTS_MIN_HEIGHT,
              PAGE_BOTTOM_OFFSET,
              DD_ELEMENTS_HEIGHT
          )
        : 0;

    return (
        <>
            <InputDropdown
                className={styles.dropdown}
                isOpen={isOpen}
                {...searchState}
                direction={isOpen ? searchState.direction : (props.defaultDir as Direction)}
                isMulti={isMultiSelect}
                triggerRef={inputRef}
                inputProps={{
                    icon: canZoom && !isOpen && (
                        <Icon
                            icon={PriorityIcon.searchThin}
                            onClick={handleZoom}
                            skin="standart"
                            componentClasses={styles.searchIcon}
                        />
                    ),
                    onFocus: handleFocusField,
                    handleSpreadsheetValues,
                    autoFocus: props.autoFocus,
                    autoSelect: props.autoFocus,
                    componentClasses: styles.input,
                    size: 'small',
                    readOnly: isSimpleChoose
                }}
                triggerButton={
                    isOpen &&
                    canZoom && (
                        <Button
                            skin="text"
                            componentClasses={styles.advancedSearch}
                            prefixIcon={PriorityIcon.F11}
                            onClick={handleZoomToAdvancedSearch}
                        >
                            {getLocalizedText(generalTexts.advancedSearch)}
                        </Button>
                    )
                }
                dropdownStyle={style}
                contentStyle={style}
                resultsAutoMaxHeight={resultsAutoMaxHeight}
                contentComponent={DropdownContent}
                isItemSelected={isItemSelected}
                onSelectItem={handleSelectItem}
                onMultiSelect={handleMultiSelect}
                onOpen={handleOpen}
                onClose={handleKeyClose}
                onZoom={handleZoom}
                onIgnoreCaseChange={handleIgnoreCaseChange}
                onSearchFromBeginningChange={handleSearchFromBeginningChange}
                onScroll={handleScroll}
                // reset the searchQuery when changing search category
                // only in procedures (needed due to the server side behaviour)
                onSearchByChange={() => handleSearchByChange('')}
                onSortByChange={handleSortByChange}
                onKeyDown={handleDropdownKeyDown}
                onSearchChange={handleInputChange}
            />
            {isMultiSelect && selected?.length > 0 && (
                <div className={styles.tagsArea}>
                    <Flex>
                        <Text skin="subtitle-2">
                            {texts.xxItems.replace('XX', selected.length)}
                        </Text>
                        <Button
                            skin="text"
                            prefixIcon={PriorityIcon.clear}
                            onClick={() => handleResetItems()}
                        >
                            {texts.clear}
                        </Button>
                    </Flex>
                    <TagsArea tags={selected} removeTag={handleClearItem} />
                </div>
            )}
        </>
    );
});

export const ProcSearch = forwardRef((props: ProcSearchInitProps, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const {fieldNum, inputIdx, operator} = props;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.addEventListener('keydown', handleKeyDown);

            const cleanupCopy = inputRef.current;
            return () => cleanupCopy.removeEventListener('keydown', handleKeyDown);
        }
        return noop;
    }, [inputRef.current, fieldNum, inputIdx, operator]);

    const handleKeyDown = event => {
        const {code} = event;
        const {fieldNum, inputIdx} = props;
        switch (code) {
            case 'F1':
                showHelp();
                break;
            case 'F9':
                invokeF9Handler(event.metaKey || event.ctrlKey, fieldNum, inputIdx);
                break;
            case 'ArrowDown':
                event.preventDefault();
                navigateToNextField(fieldNum, true);
                break;
            case 'ArrowUp':
                event.preventDefault();
                navigateToNextField(fieldNum, false);
                break;
            default:
                break;
        }
    };

    const showHelp = () => {
        helpPopoverRef.current.showHelpPopover({
            title: 'Help',
            content: props.help?.helpText,
            linkData: props.help?.helpLinks,
            onLinkClick: onHyperlinkClick,
            anchor: inputRef.current
        });
    };

    const handleFocusField = () => {
        onFieldFocus(props.fieldNum, props.inputIdx);
    };

    const handleActivateField = () => {
        inputRef.current?.focus();
        inputRef?.current?.select();
    };

    const handleZoom = () => {
        procFormZoom(props.fieldNum, props.inputIdx);
    };

    if (props.zoomtype === ZoomTypes.Search || props.zoomtype === ZoomTypes.Choose) {
        return (
            <ProcSearchDropdown
                {...props}
                key={fieldNum + inputIdx + operator}
                operator={operator}
                ref={ref}
                handleFocusField={handleFocusField}
                handleActivateField={handleActivateField}
                handleZoom={handleZoom}
                inputRef={inputRef}
            />
        );
    }
    // regular input with zoom
    return (
        <ProcSearchInput
            {...props}
            ref={ref}
            handleFocusField={handleFocusField}
            handleActivateField={handleActivateField}
            handleZoom={handleZoom}
            inputRef={inputRef}
        />
    );
});
